/**
 * 📜 EXTRA QUESTS - One-time achievements and challenges
 *
 * Extra quests are standalone achievements that don't belong to
 * the main storyline or side quest chains. They're organized
 * into categories for easy browsing.
 *
 * ADDING NEW EXTRA QUESTS:
 * 1. Add a category to extraQuestCategories (if needed)
 * 2. Add quest with category: 'categoryId'
 * 3. Optionally add hiddenUntil to hide until certain progress
 */

// Helper to generate extra quest ID
const eq = (category, num) => `extra_${category}_${String(num).padStart(2, '0')}`;

// ═══════════════════════════════════════════════════════════════════════════
// EXTRA QUEST CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════

export const extraQuestCategories = {
  milestone: {
    id: 'milestone',
    name: 'Milestones',
    icon: '🏆',
    description: 'Major achievements and accomplishments',
  },
  resource: {
    id: 'resource',
    name: 'Resource Hoarder',
    icon: '💎',
    description: 'Accumulate vast quantities of resources',
  },
  combat: {
    id: 'combat',
    name: 'Combat Mastery',
    icon: '⚔️',
    description: 'Prove yourself in battle',
  },
  collection: {
    id: 'collection',
    name: 'Collector',
    icon: '📦',
    description: 'Gather rare items and equipment',
  },
  challenge: {
    id: 'challenge',
    name: 'Challenges',
    icon: '🎯',
    description: 'Special challenges for experienced players',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// EXTRA QUESTS
// ═══════════════════════════════════════════════════════════════════════════

export const extraQuests = {
  // ═══════════════════════════════════════════════════════════════════════════
  // MILESTONE CATEGORY
  // ═══════════════════════════════════════════════════════════════════════════

  [eq('milestone', 1)]: {
    id: eq('milestone', 1),
    category: 'milestone',
    name: 'First Steps',
    description: 'Explore your first 10 tiles.',
    objectives: [{ type: 'tiles_explored', count: 10 }],
    rewards: { gold: 50 },
  },

  [eq('milestone', 2)]: {
    id: eq('milestone', 2),
    category: 'milestone',
    name: 'Novice Builder',
    description: 'Own your first 5 buildings.',
    objectives: [{ type: 'buildings_total', count: 5 }],
    rewards: { gold: 100, wood: 50 },
  },

  [eq('milestone', 3)]: {
    id: eq('milestone', 3),
    category: 'milestone',
    name: 'Tech Enthusiast',
    description: 'Research 5 technologies.',
    objectives: [{ type: 'techs_researched', count: 5 }],
    rewards: { knowledge: 100 },
  },

  [eq('milestone', 4)]: {
    id: eq('milestone', 4),
    category: 'milestone',
    name: 'Century Club',
    description: 'Explore 100 tiles.',
    objectives: [{ type: 'tiles_explored', count: 100 }],
    rewards: { gold: 2500, knowledge: 1000 },
    hiddenUntil: { tilesExplored: 50 },
  },

  [eq('milestone', 5)]: {
    id: eq('milestone', 5),
    category: 'milestone',
    name: 'Industrial Magnate',
    description: 'Own 50 buildings.',
    objectives: [{ type: 'buildings_total', count: 50 }],
    rewards: { gold: 5000, steel: 1000 },
    hiddenUntil: { buildingsOwned: 25 },
  },

  [eq('milestone', 6)]: {
    id: eq('milestone', 6),
    category: 'milestone',
    name: 'Research Pioneer',
    description: 'Research 30 technologies.',
    objectives: [{ type: 'techs_researched', count: 30 }],
    rewards: { knowledge: 5000, science: 2000 },
    hiddenUntil: { buildingsOwned: 20 },
  },

  [eq('milestone', 7)]: {
    id: eq('milestone', 7),
    category: 'milestone',
    name: 'World Conqueror',
    description: 'Explore 300 tiles and build on 200.',
    objectives: [
      { type: 'tiles_explored', count: 300 },
      { type: 'tiles_built', count: 200 },
    ],
    rewards: { gold: 25000, magic: 5000, artifacts: 1000 },
    hiddenUntil: { tilesExplored: 150 },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // RESOURCE CATEGORY
  // ═══════════════════════════════════════════════════════════════════════════

  [eq('resource', 1)]: {
    id: eq('resource', 1),
    category: 'resource',
    name: 'Wood Stockpile',
    description: 'Accumulate 10,000 wood.',
    objectives: [{ type: 'resource', target: 'wood', count: 10000 }],
    rewards: { gold: 500, tools: 100 },
  },

  [eq('resource', 2)]: {
    id: eq('resource', 2),
    category: 'resource',
    name: 'Stone Fortress',
    description: 'Accumulate 10,000 stone.',
    objectives: [{ type: 'resource', target: 'stone', count: 10000 }],
    rewards: { gold: 500, iron: 200 },
  },

  [eq('resource', 3)]: {
    id: eq('resource', 3),
    category: 'resource',
    name: 'Golden Treasury',
    description: 'Accumulate 50,000 gold.',
    objectives: [{ type: 'resource', target: 'gold', count: 50000 }],
    rewards: { gems: 500, knowledge: 1000 },
    hiddenUntil: { buildingsOwned: 15 },
  },

  [eq('resource', 4)]: {
    id: eq('resource', 4),
    category: 'resource',
    name: 'Industrial Powerhouse',
    description: 'Accumulate 5,000 steel and 3,000 machinery.',
    objectives: [
      { type: 'resource', target: 'steel', count: 5000 },
      { type: 'resource', target: 'machinery', count: 3000 },
    ],
    rewards: { gold: 5000, energy: 2000 },
    hiddenUntil: { buildingsOwned: 30 },
  },

  [eq('resource', 5)]: {
    id: eq('resource', 5),
    category: 'resource',
    name: 'Arcane Reservoir',
    description: 'Accumulate 25,000 magic and 10,000 crystals.',
    objectives: [
      { type: 'resource', target: 'magic', count: 25000 },
      { type: 'resource', target: 'crystals', count: 10000 },
    ],
    rewards: { magic: 10000, artifacts: 2000 },
    hiddenUntil: { buildingsOwned: 50 },
  },

  [eq('resource', 6)]: {
    id: eq('resource', 6),
    category: 'resource',
    name: 'Millionaire',
    description: 'Accumulate 1,000,000 gold.',
    objectives: [{ type: 'resource', target: 'gold', count: 1000000 }],
    rewards: { gems: 10000, artifacts: 5000 },
    hiddenUntil: { buildingsOwned: 60 },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // COMBAT CATEGORY
  // ═══════════════════════════════════════════════════════════════════════════

  [eq('combat', 1)]: {
    id: eq('combat', 1),
    category: 'combat',
    name: 'Dungeon Initiate',
    description: 'Clear your first dungeon.',
    objectives: [{ type: 'dungeons_cleared', count: 1 }],
    rewards: { gold: 100, starEssence: 10 },
  },

  [eq('combat', 2)]: {
    id: eq('combat', 2),
    category: 'combat',
    name: 'Boss Hunter',
    description: 'Defeat 5 dungeon bosses.',
    objectives: [{ type: 'bosses_defeated', count: 5 }],
    rewards: { gold: 500, starEssence: 50 },
    hiddenUntil: { dungeonClears: 3 },
  },

  [eq('combat', 3)]: {
    id: eq('combat', 3),
    category: 'combat',
    name: 'Level 25',
    description: 'Reach character level 25.',
    objectives: [{ type: 'character_level', count: 25 }],
    rewards: { gold: 2000, starEssence: 200 },
    hiddenUntil: { dungeonClears: 5 },
  },

  [eq('combat', 4)]: {
    id: eq('combat', 4),
    category: 'combat',
    name: 'Veteran Dungeoneer',
    description: 'Clear 25 dungeons.',
    objectives: [{ type: 'dungeons_cleared', count: 25 }],
    rewards: { gold: 5000, starEssence: 500 },
    hiddenUntil: { dungeonClears: 10 },
  },

  [eq('combat', 5)]: {
    id: eq('combat', 5),
    category: 'combat',
    name: 'Level 50',
    description: 'Reach character level 50.',
    objectives: [{ type: 'character_level', count: 50 }],
    rewards: { gold: 10000, starEssence: 1000, magic: 3000 },
    hiddenUntil: { dungeonClears: 20 },
  },

  [eq('combat', 6)]: {
    id: eq('combat', 6),
    category: 'combat',
    name: 'Dungeon Master',
    description: 'Clear 100 dungeons and defeat 50 bosses.',
    objectives: [
      { type: 'dungeons_cleared', count: 100 },
      { type: 'bosses_defeated', count: 50 },
    ],
    rewards: { gold: 50000, starEssence: 5000, artifacts: 3000 },
    hiddenUntil: { dungeonClears: 50 },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // COLLECTION CATEGORY
  // ═══════════════════════════════════════════════════════════════════════════

  [eq('collection', 1)]: {
    id: eq('collection', 1),
    category: 'collection',
    name: 'Equipped for Adventure',
    description: 'Equip items in all 12 equipment slots.',
    objectives: [{ type: 'equipment_slots_filled', count: 12 }],
    rewards: { gold: 500, starEssence: 50 },
    hiddenUntil: { dungeonClears: 1 },
  },

  [eq('collection', 2)]: {
    id: eq('collection', 2),
    category: 'collection',
    name: 'Rare Collector',
    description: 'Own 5 rare or better equipment pieces.',
    objectives: [{ type: 'equipment_rarity_min', target: 'rare', count: 5 }],
    rewards: { gold: 1000, starEssence: 100 },
    hiddenUntil: { dungeonClears: 5 },
  },

  [eq('collection', 3)]: {
    id: eq('collection', 3),
    category: 'collection',
    name: 'Epic Arsenal',
    description: 'Own 5 epic or better equipment pieces.',
    objectives: [{ type: 'equipment_rarity_min', target: 'epic', count: 5 }],
    rewards: { gold: 5000, starEssence: 500 },
    hiddenUntil: { dungeonClears: 15 },
  },

  [eq('collection', 4)]: {
    id: eq('collection', 4),
    category: 'collection',
    name: 'Legendary Hunter',
    description: 'Own 3 legendary equipment pieces.',
    objectives: [{ type: 'equipment_rarity_min', target: 'legendary', count: 3 }],
    rewards: { gold: 15000, starEssence: 1500, magic: 3000 },
    hiddenUntil: { dungeonClears: 30 },
  },

  [eq('collection', 5)]: {
    id: eq('collection', 5),
    category: 'collection',
    name: 'Mythic Collector',
    description: 'Own a mythic equipment piece.',
    objectives: [{ type: 'equipment_rarity_min', target: 'mythic', count: 1 }],
    rewards: { gold: 50000, starEssence: 5000, artifacts: 2000 },
    hiddenUntil: { dungeonClears: 50 },
  },

  [eq('collection', 6)]: {
    id: eq('collection', 6),
    category: 'collection',
    name: 'Star Force Master',
    description: 'Enhance any equipment to 15 stars.',
    objectives: [{ type: 'equipment_max_stars', count: 15 }],
    rewards: { gold: 100000, starEssence: 10000, artifacts: 5000 },
    hiddenUntil: { dungeonClears: 25 },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHALLENGE CATEGORY
  // ═══════════════════════════════════════════════════════════════════════════

  [eq('challenge', 1)]: {
    id: eq('challenge', 1),
    category: 'challenge',
    name: 'Speed Builder',
    description: 'Own 20 buildings.',
    objectives: [{ type: 'buildings_total', count: 20 }],
    rewards: { gold: 1000, knowledge: 500 },
    hiddenUntil: { buildingsOwned: 10 },
  },

  [eq('challenge', 2)]: {
    id: eq('challenge', 2),
    category: 'challenge',
    name: 'Expedition Enthusiast',
    description: 'Complete 25 expeditions.',
    objectives: [{ type: 'expeditions_total', count: 25 }],
    rewards: { gold: 2000, gems: 100 },
  },

  [eq('challenge', 3)]: {
    id: eq('challenge', 3),
    category: 'challenge',
    name: 'Biome Specialist',
    description: 'Complete expeditions in 5 different biomes.',
    objectives: [{ type: 'unique_biomes_expeditions', count: 5 }],
    rewards: { gold: 3000, knowledge: 1000 },
    hiddenUntil: { tilesExplored: 30 },
  },

  [eq('challenge', 4)]: {
    id: eq('challenge', 4),
    category: 'challenge',
    name: 'Multi-Tasker',
    description: 'Own 30 buildings, research 20 techs, and explore 75 tiles.',
    objectives: [
      { type: 'buildings_total', count: 30 },
      { type: 'techs_researched', count: 20 },
      { type: 'tiles_explored', count: 75 },
    ],
    rewards: { gold: 10000, magic: 2000, artifacts: 500 },
    hiddenUntil: { buildingsOwned: 20 },
  },

  [eq('challenge', 5)]: {
    id: eq('challenge', 5),
    category: 'challenge',
    name: 'True Completionist',
    description: 'Complete all main story quests.',
    objectives: [{ type: 'main_quests_completed', count: 120 }],
    rewards: { gold: 500000, magic: 100000, artifacts: 25000 },
    hiddenUntil: { buildingsOwned: 60 },
  },
};

export default extraQuests;
